import type { PaymentSlipDTO, PurchaseDTO, RequestPurchaseRequest } from '@brainkey/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { clientPurchasesApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

/** The client's currently-entitled purchase, or `null` before their first
 * one is verified — the signal `RequirePurchase` gates the dashboard on. */
export function useActivePurchase(): UseQueryResult<PurchaseDTO | null> {
  return useQuery({
    queryKey: queryKeys.clientPurchases.active,
    queryFn: ({ signal }) => clientPurchasesApi.getActive(signal),
  });
}

export function useMyPurchases(): UseQueryResult<PurchaseDTO[]> {
  return useQuery({
    queryKey: queryKeys.clientPurchases.list,
    queryFn: ({ signal }) => clientPurchasesApi.listMine(signal),
  });
}

export function usePurchase(id: string | null): UseQueryResult<PurchaseDTO> {
  return useQuery({
    queryKey: queryKeys.clientPurchases.detail(id ?? ''),
    queryFn: ({ signal }) => clientPurchasesApi.getById(id ?? '', signal),
    enabled: id !== null && id !== '',
    // A rejected purchase awaiting resubmission is worth polling briefly
    // after a slip upload so the status flips without a manual refresh —
    // but this is a light poll (coach review isn't instant), not tight.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'SLIP_SUBMITTED' ? 15_000 : false;
    },
  });
}

function invalidatePurchases(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.clientPurchases.active });
  void qc.invalidateQueries({ queryKey: queryKeys.clientPurchases.list });
}

export function useRequestPurchase(): UseMutationResult<
  PurchaseDTO,
  unknown,
  RequestPurchaseRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RequestPurchaseRequest) => clientPurchasesApi.request(body),
    onSuccess: () => invalidatePurchases(qc),
  });
}

export function useSubmitPaymentSlip(): UseMutationResult<
  PaymentSlipDTO,
  unknown,
  { purchaseId: string; file: File }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ purchaseId, file }) => clientPurchasesApi.submitSlip(purchaseId, file),
    onSuccess: (_slip, { purchaseId }) => {
      invalidatePurchases(qc);
      void qc.invalidateQueries({ queryKey: queryKeys.clientPurchases.detail(purchaseId) });
    },
  });
}
